import React, { useState, useRef, useEffect, useCallback } from 'react';
import { B } from '../constants';

function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoTrimmer({ videoUrl, duration, onChange, platformMarkers, cropMode, targetAspectRatio, videoAspectRatio }) {
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(duration || 30);
  const [isPlaying, setIsPlaying] = useState(false);
  const [dragging, setDragging] = useState(null);
  const videoRef = useRef(null);
  const trackRef = useRef(null);

  const maxDuration = Math.min(duration || 30, 140);

  const startTimeRef = useRef(startTime);
  const endTimeRef = useRef(endTime);
  const draggingRef = useRef(dragging);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => { startTimeRef.current = startTime; }, [startTime]);
  useEffect(() => { endTimeRef.current = endTime; }, [endTime]);
  useEffect(() => { draggingRef.current = dragging; }, [dragging]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isPlaying) {
      el.play().catch(() => setIsPlaying(false));
    } else {
      el.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (videoRef.current && !isPlaying) {
      videoRef.current.currentTime = startTime;
    }
  }, [startTime, isPlaying]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlayingRef.current) {
      videoRef.current.pause();
    } else {
      if (videoRef.current.currentTime >= endTimeRef.current || videoRef.current.currentTime < startTimeRef.current) {
        videoRef.current.currentTime = startTimeRef.current;
      }
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(prev => !prev);
  }, []);

  const handleSeeked = useCallback(() => {
    if (isPlayingRef.current && videoRef.current) {
      if (videoRef.current.currentTime >= endTimeRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.currentTime = startTimeRef.current;
      }
    }
  }, []);

  const handleSliderInteraction = useCallback((clientX) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const time = ratio * maxDuration;

    if (draggingRef.current === 'start') {
      const newStart = Math.min(time, endTimeRef.current - 1);
      setStartTime(Math.round(newStart * 10) / 10);
    } else if (draggingRef.current === 'end') {
      const newEnd = Math.max(time, startTimeRef.current + 1);
      setEndTime(Math.round(newEnd * 10) / 10);
    }
  }, [maxDuration]);

  const handleMouseDown = (handle) => (e) => {
    e.preventDefault();
    setDragging(handle);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => handleSliderInteraction(e.clientX);
    const handleMouseUp = () => {
      setDragging(null);
      onChange({
        trimStart: startTime,
        trimDuration: endTime - startTime,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleSliderInteraction, onChange, startTime, endTime]);

  useEffect(() => {
    if (!dragging) return;
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) handleSliderInteraction(e.touches[0].clientX);
    };
    const handleTouchEnd = () => {
      setDragging(null);
      onChange({
        trimStart: startTime,
        trimDuration: endTime - startTime,
      });
    };
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragging, handleSliderInteraction, onChange, startTime, endTime]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        setStartTime(prev => Math.max(0, prev - step));
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        setEndTime(prev => Math.min(maxDuration, prev + step));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [maxDuration, togglePlay]);

  const trimDuration = endTime - startTime;
  const leftPct = (startTime / maxDuration) * 100;
  const rightPct = (endTime / maxDuration) * 100;

  return (
    <div style={{ background: '#fafafa', borderRadius: 12, padding: 16, border: '1px solid #e5e0d8' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: B.night }}>
          ✂️ Trim Video
        </h4>
        <div style={{ display: 'flex', gap: 12, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: B.muted }}>
          <span>Selected: <strong style={{ color: B.forest }}>{formatTime(trimDuration)}</strong></span>
          <span>Full: <strong>{formatTime(duration || 0)}</strong></span>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: 12, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        <video
          ref={videoRef}
          src={videoUrl}
          style={{ width: '100%', maxHeight: 240, display: 'block', objectFit: 'contain' }}
          onTimeUpdate={handleSeeked}
          onSeeked={handleSeeked}
          onEnded={() => { setIsPlaying(false); }}
          preload="metadata"
        />
        <button
          onClick={togglePlay}
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {isPlaying ? '⏸' : '▶️'}
        </button>
        {dragging && (
          <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 10px', borderRadius: 6, fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
            {formatTime(dragging === 'start' ? startTime : endTime)}
          </div>
        )}
      </div>

      <div
        ref={trackRef}
        style={{ position: 'relative', height: 32, background: '#e5e0d8', borderRadius: 6, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}
      >
        {platformMarkers?.map((marker, i) => {
          const pct = (marker.duration / maxDuration) * 100;
          if (pct > 100) return null;
          return (
            <div key={i} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 2, background: marker.color, zIndex: 1, opacity: 0.7 }}
              title={`${marker.label}: ${marker.duration}s max`} />
          );
        })}
        <div style={{ position: 'absolute', left: `${leftPct}%`, right: `${100 - rightPct}%`, top: 0, bottom: 0, background: `${B.forest}44`, borderRadius: 6 }} />
        <div
          onMouseDown={handleMouseDown('start')}
          onTouchStart={() => setDragging('start')}
          style={{ position: 'absolute', left: `${leftPct}%`, top: -4, width: 16, height: 40, marginLeft: -8, background: B.forest, borderRadius: '4px 0 0 4px', cursor: 'ew-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ width: 2, height: 20, background: 'rgba(255,255,255,0.5)', borderRadius: 1 }} />
        </div>
        <div
          onMouseDown={handleMouseDown('end')}
          onTouchStart={() => setDragging('end')}
          style={{ position: 'absolute', left: `${rightPct}%`, top: -4, width: 16, height: 40, marginLeft: -8, background: B.forest, borderRadius: '0 4px 4px 0', cursor: 'ew-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ width: 2, height: 20, background: 'rgba(255,255,255,0.5)', borderRadius: 1 }} />
        </div>
        <div style={{ position: 'absolute', top: 36, left: `${leftPct}%`, transform: 'translateX(-50%)', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: B.forest, fontWeight: 600 }}>
          {formatTime(startTime)}
        </div>
        <div style={{ position: 'absolute', top: 36, left: `${rightPct}%`, transform: 'translateX(-50%)', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: B.forest, fontWeight: 600 }}>
          {formatTime(endTime)}
        </div>
      </div>

      {(cropMode || targetAspectRatio || platformMarkers) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 10 }}>
          {cropMode && targetAspectRatio && (
            <span style={{ padding: '3px 8px', borderRadius: 4, background: 'rgba(26,107,74,0.1)', color: B.forest, fontWeight: 600 }}>
              {cropMode === 'letterbox' ? '⬛' : cropMode === 'crop' ? '✂️' : '🎯'} {targetAspectRatio}
            </span>
          )}
          {platformMarkers?.map((m, i) => (
            <span key={i} style={{ padding: '3px 8px', borderRadius: 4, background: `${m.color}18`, color: m.color, fontWeight: 600 }}>
              {m.label}: {m.duration}s
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          onClick={() => {
            setStartTime(0);
            setEndTime(maxDuration);
            onChange({ trimStart: 0, trimDuration: maxDuration });
          }}
          style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, color: B.muted }}
        >
          Reset
        </button>
        <button
          onClick={togglePlay}
          style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: B.forest, color: 'white', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600 }}
        >
          {isPlaying ? '⏸ Pause' : '▶️ Preview'}
        </button>
      </div>
    </div>
  );
}
